import React, { useState } from 'react'
import { Button, Modal, Form, InputGroup } from 'react-bootstrap'
import { useToasts } from 'react-toast-notifications'
import { customerShare } from 'src/main_app/services/apis'

const CustomerShareModal = ({
  showModal,
  handleClose,
  customer,
}) => {
  const [shareData, setShareData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [shareLink, setShareLink] = useState('')
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const { addToast } = useToasts()

  const handleShareCustomer = async () => {
    if (!customer?.id || !email) return
    
    setIsLoading(true)
    try {
      const response = await customerShare(customer.id, email)
      const { new_share_code } = response.data.data
      
      setShareData({ customer_id: customer.id, code: new_share_code })
      
      // Construct the shareable link for frontend
      const baseUrl = window.location.origin

      const link = `${baseUrl}/shared-customer/${customer.id}`
      setShareLink(link)
      
      addToast('Share link generated successfully!', { 
        appearance: 'success',
        autoDismiss: true 
      })
    } catch (error) {
      console.error('Error sharing customer:', error)
      addToast('Failed to generate share link. Please try again.', { 
        appearance: 'error',
        autoDismiss: true 
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink)
      addToast('Link copied to clipboard!', { 
        appearance: 'success',
        autoDismiss: true 
      })
    } catch (error) {
      console.error('Failed to copy link:', error)
      addToast('Failed to copy link. Please copy manually.', { 
        appearance: 'error',
        autoDismiss: true 
      })
    }
  }

  const handleModalClose = () => {
    setShareData(null)
    setShareLink('')
    setEmail('')
    setEmailError('')
    handleClose()
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!email) {
      setEmailError('Please enter recipient email.')
      return
    }

    setEmailError('')
    await handleShareCustomer()
  }

  if (!showModal) return null

  return (
    <Modal show={showModal} onHide={handleModalClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Share Customer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center p-4">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Generating share link...</span>
            </div>
            <p className="mt-2">Generating share link...</p>
          </div>
        ) : (
          <div>
            {!shareLink && (
              <Form onSubmit={handleSubmit}>
                <p className="mb-3">
                  Enter the email address of the person you want to share customer{' '}
                  <strong>{customer?.trading_name || customer?.name}</strong> with.
                </p>
                <Form.Group controlId="shareCustomerEmail">
                  <Form.Label>Recipient email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    isInvalid={!!emailError}
                  />
                  <Form.Control.Feedback type="invalid">
                    {emailError}
                  </Form.Control.Feedback>
                </Form.Group>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Generating link...' : 'Generate share link'}
                </Button>
              </Form>
            )}

            {shareLink && (
              <>
                <p className="mb-3">
                  Share this link to give access to view customer details for{' '}
                  <strong>{customer?.trading_name || customer?.name}</strong>.
                  <br />
                  The recipient will be asked to confirm this email before accessing:
                  <br />
                  <strong>{email}</strong>
                </p>
                <Form.Group>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={shareLink}
                      readOnly
                      className="bg-light"
                    />
                    <InputGroup.Append>
                      <Button 
                        variant="outline-primary" 
                        onClick={handleCopyLink}
                        title="Copy to clipboard"
                      >
                        <i className="fas fa-copy"></i>
                      </Button>
                    </InputGroup.Append>
                  </InputGroup>
                </Form.Group>
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CustomerShareModal